// Shared-bag insulation line presets. Some products are bought as ONE physical bag
// (one stock SKU, e.g. KN3646 Jet Stream ULTRA blown loose-fill) but installed as
// several R-value configs, each with its own SF/bag coverage. Rather than a parent
// stock pool (unsupported/blocked) or 7 independent SKUs, each R-value is an estimate
// LINE on the same shared SKU carrying a per-line coverage override + R-value.
//
// In the estimate picker, a preset-having SKU is replaced by N synthetic options whose
// `skuId` is a COMPOSITE KEY (`<realSkuId>::<rValue>`). PricingCalculator decodes the
// key at submit time → { real skuId, coverageSfOverride, rValue }. So SkuSearchSelect
// stays untouched (it just sees more options). Keyed by SKU CODE (env-stable).

export interface LinePreset {
  rValue: string; // e.g. 'R-30'
  coverageSf: number; // SF per bag
  minThickness: string; // settled thickness, display-only (derived from rValue here)
}

export const LINE_PRESETS_BY_SKU: Record<string, LinePreset[]> = {
  KN3646: [
    { rValue: 'R-11', coverageSf: 210.8, minThickness: '4"' },
    { rValue: 'R-13', coverageSf: 175.3, minThickness: '4¾"' },
    { rValue: 'R-19', coverageSf: 119.3, minThickness: '6¾"' },
    { rValue: 'R-22', coverageSf: 102.2, minThickness: '7¾"' },
    { rValue: 'R-30', coverageSf: 73.3, minThickness: '10⅜"' },
    { rValue: 'R-38', coverageSf: 56.2, minThickness: '13"' },
    { rValue: 'R-49', coverageSf: 42.5, minThickness: '16⅝"' },
  ],
};

export function presetsForSkuCode(code: string | null | undefined): LinePreset[] {
  if (!code) return [];
  return LINE_PRESETS_BY_SKU[code.trim()] ?? [];
}

const SEP = '::';

/** Composite option key for a preset: `<realSkuId>::<rValue>`. realSkuId is a UUID
 *  (never contains `::`), so the split is unambiguous. */
export function encodePresetKey(realSkuId: string, rValue: string): string {
  return `${realSkuId}${SEP}${rValue}`;
}

/** Decode a composite preset key → { realSkuId, rValue }, or null if it's a plain skuId. */
export function decodePresetKey(key: string): { realSkuId: string; rValue: string } | null {
  const i = key.indexOf(SEP);
  if (i < 0) return null;
  return { realSkuId: key.slice(0, i), rValue: key.slice(i + SEP.length) };
}

/**
 * The editor skuId to SEED a saved field-estimate line with, when reopening an
 * estimate. SINGLE SOURCE OF TRUTH for the round-trip contract: this MUST use the
 * same "does this SKU expand into preset options?" decision as the picker's option
 * list (PricingCalculator's `flatMap` over `presetsForSkuCode`). If they disagree,
 * a saved line's seeded key won't match any option → "Unavailable SKU" + no coverage
 * sent → mispriced. Only preset-having SKUs (with an rValue) get the composite key;
 * a normal batt (e.g. B09E) that happens to carry an rValue stays a bare real skuId.
 */
export function seedSkuIdForEstimate(e: {
  skuCode?: string | null;
  skuId: string;
  rValue?: string | null;
}): string {
  return e.skuCode && e.rValue && presetsForSkuCode(e.skuCode).length > 0
    ? encodePresetKey(e.skuId, e.rValue)
    : e.skuId;
}

/**
 * Build the pricing-request line items the server needs to convert SF→bags.
 * PURE (testable): `coverageOf(skuId)` resolves the per-line SF/bag coverage —
 * for a composite preset key it's the preset coverage, for a batt it's the SKU's
 * coverage_sf, for EA/CAN it's null. The server (`enteredSfToBags`) needs BOTH
 * `enteredSf` AND a coverage to convert; this is why the caller MUST pass a
 * coverageOf backed by loaded inventory — a null coverage here drops `enteredSf`
 * (batt) or `coverageSfOverride` (preset) and the server falls back to raw SF.
 * Extracted from PricingCalculator's in-component closure (the closure-over-stale-
 * inventory was the bug that hid for three passes).
 */
export function buildPricingRequestLines(
  items: { skuId: string; quantity: string; rValue?: string | null }[],
  coverageOf: (skuId: string) => number | null,
) {
  return items.map((li) => {
    const qty = Number(li.quantity);
    const cov = coverageOf(li.skuId);
    const preset = decodePresetKey(li.skuId);
    if (preset) {
      // Shared-bag preset line: send the REAL skuId + per-line coverage override +
      // r_value; the typed value is SF (cov>0) → enteredSf, server recomputes bags.
      return {
        skuId: preset.realSkuId,
        quantity: qty,
        enteredSf: qty,
        coverageSfOverride: cov ?? undefined,
        rValue: preset.rValue,
      };
    }
    // Batting → the typed value IS SF; send enteredSf (only when coverage resolves),
    // server recomputes bags. Preserve a seeded rValue for the PDF "— R-XX" suffix.
    return cov != null
      ? { skuId: li.skuId, quantity: qty, enteredSf: qty, rValue: li.rValue ?? undefined }
      : { skuId: li.skuId, quantity: qty, rValue: li.rValue ?? undefined };
  });
}
